import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { jwtDecode } from "jwt-decode";
import appConfig from "../../../config/appConfig";
import { useChat } from "../context/ChatContext";

function buildWsBaseUrl() {
  const apiBase = (appConfig.API_BASE_URL || "").trim();

  if (!apiBase) return "";

  if (apiBase.startsWith("https://")) {
    return apiBase.replace(/^https:\/\//, "wss://");
  }

  if (apiBase.startsWith("http://")) {
    return apiBase.replace(/^http:\/\//, "ws://");
  }

  return apiBase;
}

function safeDecodeToken(token) {
  try {
    return jwtDecode(token);
  } catch {
    return null;
  }
}

function getGlobalSocketState() {
  if (!window.__crmGlobalChatSocket) {
    window.__crmGlobalChatSocket = {
      ws: null,
      ownerId: null,
    };
  }
  return window.__crmGlobalChatSocket;
}

export default function GlobalChatSocket() {
  const { t } = useTranslation();

  const {
    setGlobalEvent,
    addNotification,
    incrementUnread,
    setUnreadForThread,
    clearUnread,
    setPresenceBulk,
    setUserPresence,
    setGlobalConnected,
    activeThreadId,
    isChatPageOpen,
  } = useChat();

  const retryRef = useRef(0);
  const reconnectTimerRef = useRef(null);
  const pingTimerRef = useRef(null);
  const stoppedRef = useRef(false);
  const instanceIdRef = useRef(`gws_${Date.now()}_${Math.random().toString(36).slice(2)}`);

  const activeThreadIdRef = useRef(activeThreadId);
  const isChatPageOpenRef = useRef(isChatPageOpen);

  const audioRef = useRef(
    typeof Audio !== "undefined" ? new Audio("/notification.mp3") : null
  );

  useEffect(() => {
    activeThreadIdRef.current = activeThreadId;
  }, [activeThreadId]);

  useEffect(() => {
    isChatPageOpenRef.current = isChatPageOpen;
  }, [isChatPageOpen]);

  useEffect(() => {
    const unlockAudio = () => {
      const audio = audioRef.current;
      if (!audio) return;

      try {
        audio.volume = 0;
        const p = audio.play();

        if (p && typeof p.then === "function") {
          p.then(() => {
            audio.pause();
            audio.currentTime = 0;
            audio.volume = 1;
          }).catch(() => {});
        }
      } catch {}
    };

    window.addEventListener("click", unlockAudio, { once: true });
    return () => {
      window.removeEventListener("click", unlockAudio);
    };
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) return;

    const decoded = safeDecodeToken(token);
    const myUserId = decoded?.id ? Number(decoded.id) : null;

    const wsBase = buildWsBaseUrl();
    if (!wsBase) {
      console.warn(t("chat_socket_api_base_empty"));
      return;
    }

    stoppedRef.current = false;
    const globalState = getGlobalSocketState();

    const cleanupTimers = () => {
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }

      if (pingTimerRef.current) {
        clearInterval(pingTimerRef.current);
        pingTimerRef.current = null;
      }
    };

    const playNotificationSound = () => {
      const audio = audioRef.current;
      if (!audio) return;

      try {
        audio.currentTime = 0;
        const playPromise = audio.play();

        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      } catch {}
    };

    const attachHandlers = (ws) => {
      ws.onopen = () => {
        retryRef.current = 0;
        setGlobalConnected?.(true);

        if (pingTimerRef.current) {
          clearInterval(pingTimerRef.current);
        }

        pingTimerRef.current = setInterval(() => {
          try {
            ws.send(JSON.stringify({ type: "ping" }));
          } catch {}
        }, 25000);
      };

      ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          setGlobalEvent?.(data);

          if (data?.type === "global_state") {
            const unreadByThread = data?.unread_by_thread || {};
            const presenceByUser = data?.presence_by_user || {};

            Object.entries(unreadByThread).forEach(([threadId, count]) => {
              const tid = Number(threadId);
              const safeCount = Number(count || 0);

              if (!tid) return;

              if (safeCount > 0) {
                setUnreadForThread?.(tid, safeCount);
              } else {
                clearUnread?.(tid);
              }
            });

            const normalizedPresence = {};
            Object.entries(presenceByUser).forEach(([userId, status]) => {
              const uid = Number(userId);
              if (!uid) return;
              normalizedPresence[uid] = status === "online" ? "online" : "offline";
            });

            setPresenceBulk?.(normalizedPresence);
            return;
          }

          if (data?.type === "presence") {
            const uid = Number(data.user_id);
            if (uid) {
              setUserPresence?.(uid, data.status === "online" ? "online" : "offline");
            }
            return;
          }

          if (data?.type === "read") {
            return;
          }

          if (data?.type === "message" && data?.message) {
            const msg = data.message;
            const senderId = msg?.sender_id ? Number(msg.sender_id) : null;
            const fromMe = myUserId && senderId === myUserId;
            const threadId = Number(msg.thread_id);

            const isViewingSameThread =
              isChatPageOpenRef.current &&
              Number(activeThreadIdRef.current) === threadId;

            if (fromMe) {
              return;
            }

            if (isViewingSameThread) {
              clearUnread?.(threadId);
              return;
            }

            playNotificationSound();

            addNotification?.({
              id: `msg_${msg.id}`,
              type: "message",
              threadId,
              messageId: Number(msg.id),
              title: msg?.sender?.full_name || t("chat_new_message"),
              body: msg?.body || "",
              createdAt: msg?.created_at || new Date().toISOString(),
              sender: msg?.sender || null,
            });

            incrementUnread?.(threadId);
            return;
          }
        } catch (err) {
          console.warn(t("chat_socket_invalid_message"), err);
        }
      };

      ws.onclose = (ev) => {
        setGlobalConnected?.(false);
        cleanupTimers();

        if (globalState.ws === ws) {
          globalState.ws = null;
          globalState.ownerId = null;
        }

        if (ev.code === 1008) {
          return;
        }

        if (stoppedRef.current) return;

        const retry = Math.min(10, retryRef.current + 1);
        retryRef.current = retry;

        const delay = Math.min(15000, 500 * Math.pow(2, retry - 1));
        reconnectTimerRef.current = setTimeout(connect, delay);
      };

      ws.onerror = () => {
        try {
          ws.close();
        } catch {}
      };
    };

    const connect = () => {
      if (stoppedRef.current) return;

      if (globalState.ws && globalState.ws.readyState === WebSocket.OPEN) {
        setGlobalConnected?.(true);
        return;
      }

      if (globalState.ws && globalState.ws.readyState === WebSocket.CONNECTING) {
        return;
      }

      try {
        const url = `${wsBase}/ws/global?token=${encodeURIComponent(token)}`;
        const ws = new WebSocket(url);

        globalState.ws = ws;
        globalState.ownerId = instanceIdRef.current;

        attachHandlers(ws);
      } catch (err) {
        console.error(t("chat_socket_connect_failed"), err);
      }
    };

    connect();

    return () => {
      stoppedRef.current = true;
      cleanupTimers();

      const globalStateOnUnmount = getGlobalSocketState();

      if (globalStateOnUnmount.ownerId === instanceIdRef.current) {
        try {
          globalStateOnUnmount.ws?.close();
        } catch {}
        globalStateOnUnmount.ws = null;
        globalStateOnUnmount.ownerId = null;
      }
    };
  }, [
    addNotification,
    clearUnread,
    incrementUnread,
    setGlobalConnected,
    setGlobalEvent,
    setPresenceBulk,
    setUnreadForThread,
    setUserPresence,
    t,
  ]);

  return null;
}